import { ref } from "vue";
import { debounce, isEqual, cloneDeep } from "lodash";

/**
 * Core functionality: debouncing, retry logic, performance optimizations
 *
 * @param {Object} options Configuration options
 * @param {number} options.debounceMs Debounce delay (300-3000ms based on use case, default: 1000)
 * @param {number} options.maxRetries Maximum retry attempts with exponential backoff (default: 3)
 * @param {number} options.retryDelay Base retry delay in milliseconds (default: 1000)
 * @param {boolean} options.enableRateLimiting Protect backend resources (default: true)
 * @param {number} options.rateLimitRequests Max requests per time window (default: 20)
 * @param {number} options.rateLimitWindow Time window in ms (default: 60000)
 * @param {boolean} options.validateBeforeSave Validate data before save (default: true)
 * @param {Function} options.validator Custom validation function
 * @param {Function} options.onSaveStart Callback when save starts
 * @param {Function} options.onSaveSuccess Callback when save succeeds
 * @param {Function} options.onSaveError Callback when save fails
 */
export function useAutoSave(options = {}) {
  const {
    debounceMs = 1000,
    maxRetries = 3,
    retryDelay = 1000,
    enableRateLimiting = true,
    rateLimitRequests = 20,
    rateLimitWindow = 60000,
    validateBeforeSave = true,
    validator = null,
    onSaveStart = null,
    onSaveSuccess = null,
    onSaveError = null,
  } = options;

  // State management
  const isSaving = ref(false);
  const lastSaved = ref(null);
  const dirtyFields = ref(new Set());
  const originalData = ref({});
  const saveErrors = ref([]);

  const requestRateLimiter = ref([]);

  /**
   * Helper to check if value is plain object
   */
  const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

  /**
   * Build dirty paths by comparing new data with original
   */
  const buildDirtyPaths = (next, prev, basePath = "", out = new Set()) => {
    if (typeof next !== typeof prev) {
      if (basePath) out.add(basePath);
      return out;
    }

    if (Array.isArray(next) || Array.isArray(prev)) {
      if (!isEqual(next, prev) && basePath) out.add(basePath);
      return out;
    }

    if (isPlainObject(next) && isPlainObject(prev)) {
      processObjectDiff(next, prev, basePath, out);
      return out;
    }

    // Primitive
    if (!isEqual(next, prev) && basePath) out.add(basePath);
    return out;
  };

  /**
   * Process differences in object properties
   */
  const processObjectDiff = (next, prev, basePath, out) => {
    const keys = new Set([...Object.keys(next), ...Object.keys(prev)]);
    for (const k of keys) {
      const path = basePath ? `${basePath}.${k}` : k;
      if (isEqual(next?.[k], prev?.[k])) continue;

      if (isPlainObject(next?.[k]) && isPlainObject(prev?.[k])) {
        buildDirtyPaths(next[k], prev[k], path, out);
      } else {
        out.add(path);
      }
    }
  };

  /**
   * Rate limiting to protect backend resources
   */
  const checkAndAddToRateLimit = () => {
    if (!enableRateLimiting) return true;

    const now = Date.now();
    const windowStart = now - rateLimitWindow;

    requestRateLimiter.value = requestRateLimiter.value.filter(
      (timestamp) => timestamp > windowStart,
    );

    if (requestRateLimiter.value.length >= rateLimitRequests) {
      return false;
    }

    requestRateLimiter.value.push(now);
    return true;
  };

  /**
   * Core save execution with retry logic and better error handling
   */
  const executeSave = async (saveFunction, data, context = {}) => {
    if (isSaving.value) {
      return;
    }

    if (!checkAndAddToRateLimit()) {
      const error = new Error("Rate limit exceeded. Too many save requests.");
      error.isRateLimitError = true;
      saveErrors.value.push({
        error,
        timestamp: new Date(),
        retryAttempt: 0,
      });
      onSaveError?.(error, data, context);
      console.warn("⚠️ Rate limit exceeded. Skipping save request.");
      return;
    }

    const startTime = performance.now();

    let currentRetry = 0;
    let lastError = null;

    while (currentRetry <= maxRetries) {
      try {
        isSaving.value = true;
        onSaveStart?.(data, context);

        const result = await saveFunction(data, context);

        lastSaved.value = new Date();
        dirtyFields.value.clear();
        saveErrors.value = [];

        // Only update original data if it hasn't been explicitly set (preserve baseline)
        if (!originalData.value || Object.keys(originalData.value).length === 0) {
          originalData.value = cloneDeep(data);
        }

        onSaveSuccess?.(result, data, context);

        return result;
      } catch (error) {
        lastError = error;
        console.error(`Auto-save attempt ${currentRetry + 1} failed:`, error);

        saveErrors.value.push({
          error,
          timestamp: new Date(),
          retryAttempt: currentRetry,
          saveTime: performance.now() - startTime,
        });

        onSaveError?.(error, data, context);

        currentRetry++;

        if (currentRetry <= maxRetries) {
          const delay = retryDelay * Math.pow(2, currentRetry - 1);
          console.log(`Retrying in ${delay}ms (attempt ${currentRetry + 1}/${maxRetries + 1})`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError;
  };

  const executeSaveWithCleanup = async (saveFunction, data, context = {}) => {
    try {
      return await executeSave(saveFunction, data, context);
    } finally {
      isSaving.value = false;
    }
  };

  /**
   * Track dirty fields by comparing with original data - now with real paths
   */
  const trackDirtyFields = (newData) => {
    if (!originalData.value || typeof newData !== "object") return;

    const paths = buildDirtyPaths(newData, originalData.value);
    dirtyFields.value = paths;
  };

  /**
   * Get nested value from object using dot notation
   */
  const getNestedValue = (obj, path) => {
    const keys = path.split(".");
    let current = obj;
    for (const key of keys) {
      if (current == null) return undefined;
      current = current[key];
    }
    return current;
  };

  /**
   * Create debounced save function
   */
  const createDebouncedSave = (saveFunction, customDebounceMs) => {
    return debounce(async (data, context = {}) => {
      try {
        if (validateBeforeSave && validator) {
          const result = await validator(data, context);
          const isValid = typeof result === "boolean" ? result : result?.isValid;

          if (!isValid) {
            const message = typeof result === "object" ? result.message : "Validation failed";
            const error = new Error(message || "Validation failed");
            error.isValidationError = true;
            saveErrors.value.push({
              error,
              timestamp: new Date(),
              retryAttempt: 0,
            });
            throw error;
          }
        }

        if (dirtyFields.value.size === 0) {
          return;
        }

        const dataToSave = context.onlyDirtyFields ? getDirtyFieldsData(data) : data;

        await executeSaveWithCleanup(saveFunction, dataToSave, context);
      } catch (error) {
        onSaveError?.(error, data, context);
      }
    }, customDebounceMs || debounceMs);
  };

  /**
   * Get only the data for dirty fields - now works with real paths
   */
  const getDirtyFieldsData = (data) => {
    const dirtyData = {};
    for (const fieldPath of dirtyFields.value) {
      setNestedValue(dirtyData, fieldPath, getNestedValue(data, fieldPath));
    }
    return dirtyData;
  };

  /**
   * Set nested value in object using dot notation
   */
  const setNestedValue = (obj, path, value) => {
    const keys = path.split(".");
    const lastKey = keys.pop();

    const target = keys.reduce((current, key) => {
      current[key] = current[key] || {};
      return current[key];
    }, obj);

    target[lastKey] = value;
  };

  /**
   * Initialize tracking with initial data
   */
  const initializeTracking = (initialData) => {
    originalData.value = cloneDeep(initialData);
    dirtyFields.value.clear();
    lastSaved.value = null; // Don't mark as "saved" on init
    saveErrors.value = [];
  };

  /**
   * Clear all tracking data
   */
  const clearTracking = () => {
    originalData.value = {};
    dirtyFields.value.clear();
    saveErrors.value = [];
    lastSaved.value = null;
  };

  return {
    // State
    isSaving,
    lastSaved,
    dirtyFields,
    saveErrors,

    // Core Methods
    createDebouncedSave,

    // Tracking Methods
    trackDirtyFields,
    initializeTracking,
    clearTracking,

    // Utils
    isDirty: () => dirtyFields.value.size > 0,
    getDirtyFields: () => Array.from(dirtyFields.value),
    clearDirtyFields: () => dirtyFields.value.clear(),

    getStats: () => ({
      isDirty: dirtyFields.value.size > 0,
      dirtyFieldsCount: dirtyFields.value.size,
      lastSaved: lastSaved.value,
      errorCount: saveErrors.value.length,
      isSaving: isSaving.value,
      rateLimiterCount: requestRateLimiter.value.length,
    }),
  };
}
